import { useState, useRef } from 'react';
import * as THREE from 'three';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { parseModelFile } from './modelLoader';
import { Model3DStats } from './types';
import { MATERIAL_DENSITIES } from './constants';
import {
  UploadCloud,
  FileCode,
  Box,
  Scale,
  Activity,
  Layers,
  Trash2,
  Loader2,
  CheckCircle2,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

interface Model3DAnalyzerProps {
  onModelLoaded: (object: THREE.Object3D, stats: Model3DStats) => void;
  onClearModel: () => void;
  onApplyWeightToFilament: (grams: number) => void;
  onGoToViewer: () => void;
  currentStats: Model3DStats | null;
}

export default function Model3DAnalyzer({
  onModelLoaded,
  onClearModel,
  onApplyWeightToFilament,
  onGoToViewer,
  currentStats,
}: Model3DAnalyzerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState('PLA');
  const [infillPercent, setInfillPercent] = useState(20);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['stl', 'obj', '3mf'].includes(ext)) {
      toast.error('Formato no soportado. Debe ser .stl, .obj o .3mf');
      return;
    }

    const maxMb = ext === '3mf' ? 250 : 100;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`El archivo supera el límite de ${maxMb} MB.`);
      return;
    }

    try {
      setIsLoading(true);
      setLastFile(file);
      const density = MATERIAL_DENSITIES[selectedMaterial] || 1.24;
      const { object3D, stats } = await parseModelFile(file, density, infillPercent);
      onModelLoaded(object3D, stats);
      toast.success(`Modelo "${file.name}" analizado con éxito.`);
    } catch (err: any) {
      console.error(err);
      toast.error('Error al analizar el modelo 3D: ' + (err.message || 'Archivo corrupto o inválido'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  // Re-calculate estimated weight when material or infill changes
  const handleMaterialChange = async (mat: string) => {
    setSelectedMaterial(mat);
    if (lastFile && currentStats) {
      const density = MATERIAL_DENSITIES[mat] || 1.24;
      try {
        const { object3D, stats } = await parseModelFile(lastFile, density, infillPercent);
        onModelLoaded(object3D, stats);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleInfillChange = async (infill: number) => {
    setInfillPercent(infill);
    if (lastFile && currentStats) {
      const density = MATERIAL_DENSITIES[selectedMaterial] || 1.24;
      try {
        const { object3D, stats } = await parseModelFile(lastFile, density, infill);
        onModelLoaded(object3D, stats);
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".stl,.obj,.3mf"
        className="hidden"
        onChange={handleFileChange}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl p-6 transition-all text-center ${
          isDragging
            ? 'border-primary bg-primary/5 scale-[0.99]'
            : 'border-border/80 bg-card/60 hover:border-primary/50 hover:bg-accent/20'
        }`}
      >
        <div className="max-w-md mx-auto flex flex-col items-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-3 shadow-sm">
            {isLoading ? (
              <Loader2 className="w-7 h-7 animate-spin" />
            ) : (
              <UploadCloud className="w-7 h-7" />
            )}
          </div>

          <h4 className="font-semibold text-base text-foreground mb-1">
            Analizador de modelo 3D
          </h4>
          <p className="text-xs text-muted-foreground mb-3">
            Formatos: STL, OBJ y 3MF. Hasta 100 MB, o 250 MB en 3MF.
          </p>

          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="gap-2 shadow-sm font-semibold"
          >
            <FileCode className="w-4 h-4" />
            Elegir archivo
          </Button>

          {!currentStats && !isLoading && (
            <p className="text-xs text-muted-foreground/80 mt-3 italic">
              Todavía no elegiste ningún archivo.
            </p>
          )}
        </div>
      </div>

      {/* Stats card when model is loaded */}
      {currentStats && (
        <Card className="border border-border/80 bg-card/95 shadow-sm overflow-hidden">
          <div className="bg-primary/5 border-b border-border/60 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-semibold text-sm truncate max-w-[260px] md:max-w-md">
                {currentStats.fileName}
              </span>
              <Badge variant="outline" className="text-[11px] uppercase tracking-wider font-mono">
                {currentStats.fileType}
              </Badge>
              <span className="text-xs text-muted-foreground">
                ({(currentStats.fileSizeBytes / (1024 * 1024)).toFixed(2)} MB)
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setLastFile(null);
                onClearModel();
              }}
              className="text-muted-foreground hover:text-destructive h-8 px-2"
              title="Quitar modelo"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <CardContent className="p-5 space-y-5">
            {/* Main Metrics Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-accent/40 border border-border/50">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Box className="w-3.5 h-3.5 text-primary" />
                  <span>Dimensiones (X × Y × Z)</span>
                </div>
                <p className="text-sm font-bold font-mono text-foreground">
                  {currentStats.dimensions.x} × {currentStats.dimensions.y} × {currentStats.dimensions.z}
                  <span className="text-xs font-normal text-muted-foreground ml-1">mm</span>
                </p>
              </div>

              <div className="p-3 rounded-xl bg-accent/40 border border-border/50">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Layers className="w-3.5 h-3.5 text-primary" />
                  <span>Volumen Total</span>
                </div>
                <p className="text-sm font-bold font-mono text-foreground">
                  {currentStats.volumeCm3.toLocaleString('es-AR')}
                  <span className="text-xs font-normal text-muted-foreground ml-1">cm³</span>
                </p>
              </div>

              <div className="p-3 rounded-xl bg-accent/40 border border-border/50">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <Activity className="w-3.5 h-3.5 text-primary" />
                  <span>Superficie</span>
                </div>
                <p className="text-sm font-bold font-mono text-foreground">
                  {currentStats.surfaceAreaCm2.toLocaleString('es-AR')}
                  <span className="text-xs font-normal text-muted-foreground ml-1">cm²</span>
                </p>
              </div>

              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-1.5 text-xs text-emerald-800 dark:text-emerald-400 mb-1">
                  <Scale className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-semibold">Peso Estimado</span>
                </div>
                <p className="text-base font-extrabold font-mono text-emerald-700 dark:text-emerald-400">
                  {currentStats.estimatedWeightGrams}
                  <span className="text-xs font-normal ml-1">gramos</span>
                </p>
              </div>
            </div>

            {/* Infill & Material Density Tweaker */}
            <div className="bg-background/80 rounded-xl p-4 border border-border/60 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Material para cálculo</Label>
                  <select
                    value={selectedMaterial}
                    onChange={(e) => handleMaterialChange(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-input bg-card text-xs font-medium focus:ring-1 focus:ring-primary outline-none"
                  >
                    {Object.keys(MATERIAL_DENSITIES).map((mat) => (
                      <option key={mat} value={mat}>
                        {mat} ({MATERIAL_DENSITIES[mat]} g/cm³)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <Label className="text-xs">Relleno (Infill)</Label>
                    <span className="font-mono font-bold text-foreground">{infillPercent}%</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[15, 20, 30, 50, 100].map((val) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => handleInfillChange(val)}
                        className={`px-2 py-1 text-xs rounded-md font-medium border transition-all ${
                          infillPercent === val
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card text-muted-foreground border-border hover:bg-accent'
                        }`}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2.5 w-full md:w-auto justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onGoToViewer}
                  className="gap-1.5 text-xs"
                >
                  <Eye className="w-3.5 h-3.5 text-primary" />
                  Ver en Visor 3D
                </Button>

                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    onApplyWeightToFilament(currentStats.estimatedWeightGrams);
                    toast.success(`Se aplicaron ${currentStats.estimatedWeightGrams}g al Filamento 1.`);
                  }}
                  className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Aplicar gramos al Filamento 1
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
